import type { HostSlotReasonCode } from './hostSlotPort.js';

export const TELEMETRY_ACTION_NAME = 'mfeLoader';

export type OutcomeKind =
  | 'ready'
  | 'fallback'
  | 'waiting'
  | 'unmounted'
  | 'output-dropped';

/** Reason names beyond the slot's reason codes. */
export type OutcomeReason =
  | HostSlotReasonCode
  | 'consent-revoked'
  | 'partner-empty';

/**
 * One event per placement outcome. Attributes name inputs, never carry their
 * values, and hold no identity data.
 */
export interface OutcomeAttributes {
  readonly outcome: OutcomeKind;
  readonly reason?: OutcomeReason;
  readonly slot: string;
  readonly tenantSlug?: string;
  readonly moduleId?: string;
  readonly version?: string;
  readonly integration?: string;
  readonly loadMs?: number;
  readonly mountMs?: number;
  readonly readyMs?: number;
  readonly renderedWidth?: number;
  readonly renderedHeight?: number;
  readonly grewBeyondReserved?: boolean;
  readonly missingInputs?: readonly string[];
  readonly invalidInputs?: readonly string[];
  /** The output a dropped emit named. Left out unless it is a plausible name. */
  readonly output?: string;
  /** Why an emit was dropped or a partner ended the placement, from a fixed set. Never carries payload content. */
  readonly detail?: string;
}

export interface ErrorAttributes {
  readonly slot: string;
  readonly moduleId?: string;
  readonly version?: string;
  readonly reason: string;
}

export interface Telemetry {
  outcome(attributes: OutcomeAttributes): void;
  error(error: unknown, attributes: ErrorAttributes): void;
}

export const noopTelemetry: Telemetry = {
  outcome: () => undefined,
  error: () => undefined,
};

interface NewRelicAgent {
  addPageAction?: (name: string, attributes?: Record<string, unknown>) => void;
  noticeError?: (error: Error, attributes?: Record<string, unknown>) => void;
}

function toAttributeBag(
  attributes: OutcomeAttributes | ErrorAttributes,
): Record<string, unknown> {
  const bag: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attributes)) {
    if (value === undefined) {
      continue;
    }
    bag[key] = Array.isArray(value) ? value.join(',') : value;
  }
  return bag;
}

function toError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * What New Relic receives for a loader fault. The message names the module,
 * version and reason and nothing else, so each fault of each module version
 * is its own error group, and alerts on JavaScriptError name the module.
 * The original error stays on `cause` and in the stack.
 */
export class MicroFrontendError extends Error {
  readonly reason: string;

  constructor(error: unknown, attributes: ErrorAttributes) {
    const cause = toError(error);
    const module = attributes.moduleId ?? attributes.slot;
    const version = attributes.version ? `@${attributes.version}` : '';
    super(`Micro frontend ${module}${version}: ${attributes.reason}`, {
      cause,
    });
    this.name = 'MicroFrontendError';
    this.reason = attributes.reason;
    if (cause.stack) {
      this.stack = `${this.name}: ${this.message}\nCaused by: ${cause.stack}`;
    }
  }
}

/**
 * Reports through `window.newrelic`. A missing or failing agent never
 * affects the page.
 */
export function createNewRelicTelemetry(
  getAgent: () => NewRelicAgent | undefined = () =>
    typeof window === 'undefined'
      ? undefined
      : (window as unknown as { newrelic?: NewRelicAgent }).newrelic,
): Telemetry {
  return {
    outcome(attributes) {
      try {
        getAgent()?.addPageAction?.(
          TELEMETRY_ACTION_NAME,
          toAttributeBag(attributes),
        );
      } catch {
        // Telemetry must not affect the page.
      }
    },
    error(error, attributes) {
      try {
        getAgent()?.noticeError?.(
          new MicroFrontendError(error, attributes),
          toAttributeBag(attributes),
        );
      } catch {
        // Telemetry must not affect the page.
      }
    },
  };
}

/** Wraps a telemetry port so a throwing implementation never escapes. */
export function guardTelemetry(inner: Telemetry): Telemetry {
  return {
    outcome(attributes) {
      try {
        inner.outcome(attributes);
      } catch {
        // Telemetry must not affect the page.
      }
    },
    error(error, attributes) {
      try {
        inner.error(error, attributes);
      } catch {
        // Telemetry must not affect the page.
      }
    },
  };
}
